import { FC, useEffect, useMemo, useRef, useState } from 'react'
import { FormikContextType, useFormik } from 'formik'
import { enqueueSnackbar } from 'notistack'

import { DDELicensePeriodResponse, DDELicenseResponse } from '@img-arena/consoles-api-dde'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import { Divider, Stack, Typography, useTheme } from '@mui/material'
import { ConsoleDialog } from '@/components/ConsoleDialog'
import { CompetitionsDialog } from '@/components/forms/LicenseForm/CompetitionsDialog/CompetitionsDialog'
import { LicenseCompetitionsRadioGroup } from '@/components/inputs/CompetitionsRadioGroup'
import { InputSectionDivider } from '@/components/inputs/InputSectionDivider'
import { LicensePropertyAutocomplete } from '@/components/inputs/LicensePropertyAutocomplete'
import { LicensingPeriods } from '@/components/inputs/LicensingPeriods'
import { LicensePeriodType } from '@/components/inputs/LicensingPeriods/dde/LicensingPeriod.dde'
import { FormDialogProps } from '@/types/forms'
import { LicenseProperty } from '@/utils/api/licenseProperties'
import { formikSaveButtonProps } from '@/utils/forms'
import { onFieldChange } from '@/utils/forms/formik'
import { getLicensePropertySport, isMsfpLicense } from '@/utils/licenseUtils'
import { LicenseRow } from '@/views/Operators/Operator/Licenses/licenses.dde.table'

import { COMPETITIONS, CompetitionsSelectType, USE_ALL_COMPETITIONS_MAP } from './constants'
import { licenseFormValidation } from './licenseForm.validation'
import { saveDDELicense, updateDDELicense } from './utils'

const { SELECT_ALL, COVER_SELECTED } = COMPETITIONS

type LicenseFormValues = {
  sportLicenseProperty: LicenseProperty | null
  useAllCompetitions: CompetitionsSelectType
  competitions: string[]
  licensePeriods: LicensePeriodType[]
}

type Data = {
  operatorId: number
  licenseProperty?: string
  operatorLicenses?: DDELicenseResponse[]
}

export type DDELicenseFormProps = FormDialogProps<LicenseRow, Data>

export const DDELicenseForm: FC<DDELicenseFormProps> = ({
  open,
  onClose,
  onSubmit,
  data,
  defaultValues
}) => {
  const { operatorId } = data || {}
  const theme = useTheme()
  const isEditMode = !!defaultValues?.id
  const hasInitializedCompetitions = useRef(false)
  const [openCompetitionsDialog, setOpenCompetitionsDialog] = useState(false)

  const currentLicense = data.operatorLicenses?.find(
    (license) => license.property === data.licenseProperty
  )

  const getUseAllCompetitions = () => {
    if (!currentLicense) {
      return SELECT_ALL
    } else {
      const subscription = currentLicense.licensingPeriods.find(
        (licensePeriod) => licensePeriod.subscription !== undefined
      )

      if (!subscription) {
        return SELECT_ALL
      } else {
        if (subscription.subscription?.type === 'COMPETITIONS_ALL') {
          return SELECT_ALL
        } else return COVER_SELECTED
      }
    }
  }

  const formik = useFormik<LicenseFormValues>({
    initialValues: {
      sportLicenseProperty: currentLicense?.property
        ? { label: currentLicense.property, value: currentLicense.property }
        : null,
      useAllCompetitions: getUseAllCompetitions(),
      competitions: currentLicense
        ? (() => {
            const subscription = currentLicense.licensingPeriods.find(
              (licensePeriod) => licensePeriod.subscription !== undefined
            )
            return subscription?.subscription?.competitionIds || []
          })()
        : [],
      licensePeriods: currentLicense
        ? currentLicense.licensingPeriods.map((licensePeriod) => ({
            id: licensePeriod.id,
            unlimited: licensePeriod.unlimited,
            suspended: licensePeriod.suspended,
            year: new Date(licensePeriod.startDate).getFullYear().toString(),
            startDate: licensePeriod.startDate,
            endDate: licensePeriod.endDate,
            numOfBookedEvents: licensePeriod.numOfBookedEvents,
            numOfConsumedEvents: licensePeriod.numOfConsumedEvents,
            numOfEvents: licensePeriod.numOfEvents,
            subscription: licensePeriod.subscription
          }))
        : []
    },
    validationSchema: licenseFormValidation.DDE,
    enableReinitialize: false,
    onSubmit: async ({
      sportLicenseProperty,
      useAllCompetitions,
      competitions,
      licensePeriods
    }) => {
      if (!sportLicenseProperty || !operatorId) return

      if (currentLicense && !hasFormChanged) {
        enqueueSnackbar('No changes detected. Please modify the form before saving.', {
          variant: 'info'
        })
        return
      }

      try {
        const competitionsSelectType = USE_ALL_COMPETITIONS_MAP[useAllCompetitions]

        if (currentLicense) {
          await updateDDELicense({
            operatorId: operatorId.toString(),
            licenseProperty: { label: currentLicense.property, value: currentLicense.property },
            licensePeriods: licensePeriods.map((licensePeriod) => ({
              id: licensePeriod.id || null,
              numOfBookedEvents: licensePeriod.numOfBookedEvents || 0,
              numOfConsumedEvents: licensePeriod.numOfConsumedEvents || 0,
              unlimited: licensePeriod.unlimited ? licensePeriod.unlimited : false,
              suspended: licensePeriod.suspended ? licensePeriod.suspended : false,
              numOfEvents: licensePeriod.numOfEvents || 0,
              startDate: licensePeriod.startDate,
              endDate: licensePeriod.endDate,
              subscription: licensePeriod.subscription
            })) as (DDELicensePeriodResponse & { id: number | null })[],
            selectedCompetitionsOrSeasons: [],
            useAllCompetitions: competitionsSelectType,
            competitionIds: competitions
          })
        } else {
          await saveDDELicense({
            operatorId,
            competitions: [],
            licensePeriods: licensePeriods.map((licensePeriod) => ({
              id: null,
              numOfBookedEvents: licensePeriod.numOfBookedEvents || 0,
              numOfConsumedEvents: licensePeriod.numOfConsumedEvents || 0,
              unlimited: licensePeriod.unlimited ? licensePeriod.unlimited : false,
              suspended: licensePeriod.suspended ? licensePeriod.suspended : false,
              numOfEvents: licensePeriod.numOfEvents || 0,
              startDate: licensePeriod.startDate,
              endDate: licensePeriod.endDate
            })),
            licenseProperty: sportLicenseProperty.value.toString(),
            useAllCompetitions: competitionsSelectType
          })
        }

        onSubmit?.()

        enqueueSnackbar(`Successfully ${currentLicense ? 'updated' : 'added'} license`, {
          variant: 'success'
        })
      } catch (error) {
        enqueueSnackbar(String(error), { variant: 'error' })
        throw error
      }
    }
  })

  const hasFormChanged = useMemo(() => {
    if (!currentLicense) return true

    const originalCompetitions = (() => {
      const subscription = currentLicense.licensingPeriods.find(
        (licensePeriod) => licensePeriod.subscription !== undefined
      )
      return subscription?.subscription?.competitionIds || []
    })()
    const originalLicensePeriods = currentLicense.licensingPeriods.map((period) => ({
      id: period.id,
      unlimited: period.unlimited,
      suspended: period.suspended,
      year: new Date(period.startDate).getFullYear().toString(),
      startDate: period.startDate,
      endDate: period.endDate,
      numOfBookedEvents: period.numOfBookedEvents,
      numOfConsumedEvents: period.numOfConsumedEvents,
      numOfEvents: period.numOfEvents,
      subscription: period.subscription
    }))

    const currentFormData = {
      sportLicenseProperty: currentLicense.property
        ? { label: currentLicense.property, value: currentLicense.property }
        : null,
      useAllCompetitions: getUseAllCompetitions(),
      competitions: originalCompetitions,
      licensePeriods: originalLicensePeriods
    }

    return JSON.stringify(formik.values) !== JSON.stringify(currentFormData)
  }, [formik.values, currentLicense])

  useEffect(() => {
    if (open) {
      hasInitializedCompetitions.current = false
    }
  }, [open])

  const { sportLicenseProperty, useAllCompetitions, competitions, licensePeriods } = formik.values
  const competitionsSportType =
    getLicensePropertySport(sportLicenseProperty ? sportLicenseProperty.value.toString() : '') || ''

  const handleSportLicensePropertySelect = (
    formik: FormikContextType<LicenseFormValues>,
    sportLicenseProperty: LicenseProperty | null
  ) => {
    if (!sportLicenseProperty) return

    if (!isMsfpLicense(sportLicenseProperty.value)) {
      onFieldChange(formik, 'sportLicenseProperty', sportLicenseProperty)
      onFieldChange(formik, 'useAllCompetitions', SELECT_ALL)
      onFieldChange(formik, 'competitions', [])
    } else {
      onFieldChange(formik, 'sportLicenseProperty', sportLicenseProperty)
      onFieldChange(formik, 'competitions', [])
    }
  }
  return (
    <ConsoleDialog
      open={open}
      onClose={() => {
        formik.resetForm()
        hasInitializedCompetitions.current = false
        onClose?.()
      }}
      SaveButtonProps={{
        ...formikSaveButtonProps(formik),
        disabled:
          currentLicense && !hasFormChanged
            ? true
            : formikSaveButtonProps(formik).disabled ||
              (formik.values.useAllCompetitions !== COMPETITIONS.SELECT_ALL &&
                isMsfpLicense(formik.values.sportLicenseProperty?.value.toString()) &&
                formik.values.competitions.length === 0)
      }}
      title={isEditMode ? 'Edit License' : 'Add License'}
      dialogSize="md"
    >
      <Stack direction="column" spacing={2}>
        <LicensePropertyAutocomplete.DDE
          sportLicenseProperty={sportLicenseProperty}
          editMode={!!currentLicense}
          onChange={(value) =>
            handleSportLicensePropertySelect(formik, {
              label: value?.label || '',
              value: value?.id || ''
            })
          }
          onClear={() => onFieldChange(formik, 'sportLicenseProperty', null)}
          operatorId={operatorId}
        />
        <Stack direction="row" spacing={2}>
          <Stack direction="column" width="40%">
            <InputSectionDivider label="Competitions" sx={{ mt: -0.5 }} />
            <LicenseCompetitionsRadioGroup
              value={useAllCompetitions}
              onChange={(value) => {
                onFieldChange(formik, 'useAllCompetitions', value)
                formik.validateForm()
              }}
              disabled={!isMsfpLicense(sportLicenseProperty?.value.toString())}
            />
            {useAllCompetitions !== SELECT_ALL && (
              <Stack
                padding={1}
                flexDirection="column"
                borderRadius={1}
                gap={1}
                sx={{
                  boxShadow: 1,
                  cursor: 'pointer',
                  backgroundColor: theme.palette.mode === 'light' ? 'background.paper' : 'grey.900'
                }}
                onClick={() => setOpenCompetitionsDialog(true)}
              >
                <Stack flexDirection="row" gap={0.5}>
                  <Typography>Selected competitions:</Typography>
                  <Typography fontWeight="bold">{competitions.length}</Typography>
                </Stack>
                <Stack flexDirection="row" alignItems="center" gap={0.5}>
                  <Typography sx={{ color: theme.palette.grey['500'], fontSize: 10 }}>
                    Click to select competitions
                  </Typography>
                  <OpenInNewIcon sx={{ color: theme.palette.grey['500'], fontSize: 10 }} />
                </Stack>
              </Stack>
            )}
          </Stack>

          <Divider orientation="vertical" flexItem />

          <Stack direction="column" width="60%">
            <InputSectionDivider label="Licensing Periods" sx={{ mt: -0.5 }} />
            <LicensingPeriods.DDE
              defaultValue={licensePeriods}
              onChange={(value) => onFieldChange(formik, 'licensePeriods', value)}
              height={300}
            />
          </Stack>
        </Stack>
      </Stack>
      {openCompetitionsDialog && (
        <CompetitionsDialog
          open={openCompetitionsDialog}
          onClose={() => {
            setOpenCompetitionsDialog(false)
          }}
          sportType={competitionsSportType}
          selectedCompetitions={competitions}
          onSave={(selectedCompetitions) => {
            onFieldChange(formik, 'competitions', selectedCompetitions)
            formik.validateForm()
          }}
        />
      )}
    </ConsoleDialog>
  )
}
